import React, { useEffect, useState } from "react";
import { api, fmtINR, todayISO } from "../lib/api";
import { Save, Newspaper, TrendingUp, TrendingDown, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "chicken", label: "Chicken", color: "sky" },
];

export default function PaperRates() {
  const [date, setDate] = useState(todayISO());
  const [rates, setRates] = useState({});
  const [notes, setNotes] = useState({});
  const [history, setHistory] = useState({});

  const load = async () => {
    const res = await api.get(`/paper-rates?date=${date}`);
    const map = {};
    const noteMap = {};
    res.data.forEach((r) => {
      map[r.category] = r.rate_per_kg;
      noteMap[r.category] = r.notes || "";
    });
    setRates(map);
    setNotes(noteMap);

    // Load 30-day history for trend
    const hist = {};
    await Promise.all(
      CATEGORIES.map(async (c) => {
        const r = await api.get(`/paper-rates/history?category=${c.key}&days=30`);
        hist[c.key] = r.data;
      })
    );
    setHistory(hist);
  };

  useEffect(() => {
    load();
  }, [date]);


  const saveAll = async () => {
    const items = CATEGORIES.filter((c) => rates[c.key] !== undefined && rates[c.key] !== "")
      .map((c) => ({
        date,
        category: c.key,
        rate_per_kg: parseFloat(rates[c.key]) || 0,
        notes: notes[c.key] || "",
      }));
    if (items.length === 0) {
      toast.error("Enter at least one paper rate");
      return;
    }
    await api.post("/paper-rates/bulk", items);
    toast.success(`Saved ${items.length} paper rates for ${date}`);
    load();
  };

  const deleteRate = async (rateDate, category) => {
    if (!window.confirm(`Delete ${category} paper rate for ${rateDate}?`)) return;
    await api.delete(`/paper-rates?date=${rateDate}&category=${category}`);
    toast.success("Paper rate deleted");
    // Clear local form values if currently viewed date was deleted
    if (rateDate === date) {
      setRates((r) => { const x = { ...r }; delete x[category]; return x; });
      setNotes((n) => { const x = { ...n }; delete x[category]; return x; });
    }
    load();
  };

  const deltaFor = (cat) => {
    const h = history[cat] || [];
    if (h.length < 2) return null;
    const last = h[h.length - 1];
    const prev = h[h.length - 2];
    const delta = (last.rate_per_kg || 0) - (prev.rate_per_kg || 0);
    return { delta, prevRate: prev.rate_per_kg, prevDate: prev.date };
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto page-enter" data-testid="paper-rates-page">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
            <Newspaper className="h-3 w-3" /> Market Base Rate
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mt-1">
            Paper Rate
          </h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-xl">
            Enter today&apos;s market paper rate per category. Wholesale customer prices are auto-computed as
            <b> paper rate × customer multiplier</b> (configure in <i>Customers → Multipliers</i>).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-wider text-zinc-500">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="paper-date"
            className="border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 max-w-md">
        {CATEGORIES.map((c) => {
          const delta = deltaFor(c.key);
          return (
            <div
              key={c.key}
              className="border border-zinc-200 bg-white p-5"
              data-testid={`paper-card-${c.key}`}
            >
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
                {c.label}
              </div>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-3xl font-bold font-mono-num">₹</span>
                <input
                  type="number"
                  step="1"
                  placeholder="0"
                  value={rates[c.key] ?? ""}
                  onChange={(e) => setRates((r) => ({ ...r, [c.key]: e.target.value }))}
                  data-testid={`paper-rate-${c.key}`}
                  className="text-4xl font-display font-bold font-mono-num w-full border-b-2 border-zinc-200 focus:border-sky-600 outline-none py-1"
                />
                <span className="text-xs uppercase tracking-wider text-zinc-500">/kg</span>
              </div>
              <input
                placeholder="Notes (optional, e.g. supplier name)"
                value={notes[c.key] ?? ""}
                onChange={(e) => setNotes((n) => ({ ...n, [c.key]: e.target.value }))}
                data-testid={`paper-notes-${c.key}`}
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
              />
              {delta && (
                <div className={`mt-3 flex items-center gap-1 text-xs ${delta.delta > 0 ? "text-rose-600" : delta.delta < 0 ? "text-emerald-600" : "text-zinc-500"}`}>
                  {delta.delta > 0 ? <TrendingUp className="h-3 w-3" /> : delta.delta < 0 ? <TrendingDown className="h-3 w-3" /> : null}
                  <span>
                    {delta.delta === 0
                      ? "Same as last entry"
                      : `${delta.delta > 0 ? "+" : ""}₹${Math.round(delta.delta)} vs ${delta.prevDate}`}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        onClick={saveAll}
        data-testid="paper-save"
        className="mt-6 bg-sky-600 text-white px-6 py-3 uppercase tracking-wider hover:bg-sky-700 flex items-center gap-2"
      >
        <Save className="h-4 w-4" />
        Save Paper Rates
      </button>

      {/* History strip */}
      <div className="mt-10">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
          Last 30 Days
        </div>
        <div className="grid grid-cols-1 max-w-md">
          {CATEGORIES.map((c) => {
            const h = history[c.key] || [];
            return (
              <div key={c.key} className="border border-zinc-200 bg-white p-4" data-testid={`paper-history-${c.key}`}>
                <div className="text-xs font-semibold uppercase tracking-wider mb-2">{c.label}</div>
                {h.length === 0 ? (
                  <div className="text-xs text-zinc-400">No history</div>
                ) : (
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-xs">
                      <tbody>
                        {[...h].reverse().map((r) => (
                          <tr key={r.date} className="border-b border-zinc-100 group">
                            <td className="py-1 text-zinc-500">{r.date}</td>
                            <td className="py-1 text-right font-mono-num font-semibold">
                              {fmtINR(r.rate_per_kg)}
                            </td>
                            <td className="py-1 text-right w-6">
                              <button
                                onClick={() => deleteRate(r.date, c.key)}
                                className="text-zinc-300 hover:text-rose-600 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity"
                                data-testid={`paper-delete-${c.key}-${r.date}`}
                                title="Delete this paper rate"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
